import type { SVGProps } from 'react'

import {
  CHART_GRID_STROKE,
  CHART_GRID_STROKE_DASH,
  CHART_GRID_STROKE_WIDTH,
  CHART_GRID_ZERO_STROKE,
} from './chart.constants'

const PIXEL_MATCH_TOL = 1.5

type AxisLike = {
  type?: string
  domain?: unknown
  scale?: (v: number) => unknown
}

interface GridLineProps extends SVGProps<SVGLineElement> {
  key?: string | number
  index?: number
  xAxis?: null | AxisLike
  yAxis?: null | AxisLike
}

const isCategoryAxis = (axis: AxisLike | null | undefined) =>
  axis?.type === 'category'

const domainIncludesZero = (axis: AxisLike | null | undefined): boolean => {
  const d = axis?.domain
  if (!Array.isArray(d) || d.length < 2) return true
  const a = Number(d[0])
  const b = Number(d[d.length - 1])
  if (!Number.isFinite(a) || !Number.isFinite(b)) return true
  const lo = Math.min(a, b)
  const hi = Math.max(a, b)
  return lo <= 0 && hi >= 0
}

const scaleToPixel = (
  axis: AxisLike | null | undefined,
  value: number,
): number | undefined => {
  const fn = axis?.scale
  if (typeof fn !== 'function') return undefined
  const p = fn(value)
  return typeof p === 'number' && Number.isFinite(p) ? p : undefined
}

const isNear = (a: number, b: number) => Math.abs(a - b) <= PIXEL_MATCH_TOL

/** Dashed grid line; solid neutral stroke where the line sits at numeric value 0. */
const gridLineOnValueAxis = (which: 'x' | 'y') => (props: GridLineProps) => {
  const { x1, y1, x2, y2, key } = props
  const axis = which === 'y' ? props.yAxis : props.xAxis
  const rawCoord = which === 'y' ? y1 : x1
  const valuePixel =
    typeof rawCoord === 'number' && Number.isFinite(rawCoord)
      ? rawCoord
      : Number(rawCoord)

  if (isCategoryAxis(axis) || !domainIncludesZero(axis)) {
    return (
      <line
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        key={key}
        stroke={CHART_GRID_STROKE}
        strokeWidth={CHART_GRID_STROKE_WIDTH}
        strokeDasharray={CHART_GRID_STROKE_DASH}
        fill="none"
      />
    )
  }

  const zeroPixel = scaleToPixel(axis, 0)
  const onZero =
    zeroPixel !== undefined &&
    Number.isFinite(valuePixel) &&
    isNear(valuePixel, zeroPixel)

  return (
    <line
      x1={x1}
      y1={y1}
      x2={x2}
      y2={y2}
      key={key}
      stroke={onZero ? CHART_GRID_ZERO_STROKE : CHART_GRID_STROKE}
      strokeWidth={CHART_GRID_STROKE_WIDTH}
      strokeDasharray={onZero ? undefined : CHART_GRID_STROKE_DASH}
      fill="none"
    />
  )
}

/**
 * Vertical grid for horizontal bar charts (value on X). Same coordinates as default vertical grid;
 * `index === 0` is drawn solid as the zero-value line.
 */
const horizontalBarVerticalLine = (props: GridLineProps) => {
  const { x1, y1, x2, y2, key, index } = props
  return (
    <line
      key={key}
      x1={x1}
      y1={y1}
      x2={x2}
      y2={y2}
      stroke={index === 0 ? CHART_GRID_ZERO_STROKE : CHART_GRID_STROKE}
      strokeDasharray={index === 0 ? undefined : CHART_GRID_STROKE_DASH}
      strokeWidth={CHART_GRID_STROKE_WIDTH}
      fill="none"
    />
  )
}

/** Custom `horizontal` / `vertical` renderers for Recharts `CartesianGrid`. */
export const ChartGrid = {
  horizontalLine: gridLineOnValueAxis('y'),
  verticalLine: gridLineOnValueAxis('x'),
  horizontalBarVerticalLine,
}
